import React, { useState } from 'react';
import { X, User, Search } from 'lucide-react';
import { Button } from '../ui/Button';

interface RiskProspect {
  id: string;
  first_name: string;
  last_name: string;
  // Other properties not needed for this component
  [key: string]: any;
}

interface AssignLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  prospect: RiskProspect;
  onAssign: (assignee: string) => void;
}

const AssignLeadModal: React.FC<AssignLeadModalProps> = ({ 
  isOpen, 
  onClose, 
  prospect, 
  onAssign 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeamMember, setSelectedTeamMember] = useState<string | null>(null);
  
  // Mock team members data
  const teamMembers = [
    { id: '1', name: 'Sarah Johnson', role: 'Financial Advisor', avatar: null },
    { id: '2', name: 'Michael Brown', role: 'Senior Advisor', avatar: null },
    { id: '3', name: 'David Wilson', role: 'Investment Specialist', avatar: null },
    { id: '4', name: 'Emily Davis', role: 'Retirement Planner', avatar: null },
    { id: '5', name: 'Robert Taylor', role: 'Tax Specialist', avatar: null }
  ];
  
  // Filter team members based on search term
  const filteredTeamMembers = teamMembers.filter(member => 
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAssign = () => {
    if (selectedTeamMember) {
      const teamMember = teamMembers.find(m => m.id === selectedTeamMember);
      if (teamMember) {
        onAssign(teamMember.name);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Assign Lead</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-gray-600">
            Assigning <span className="font-medium text-gray-900">{prospect.first_name} {prospect.last_name}</span> to a team member
          </p>
        </div>
        
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search team members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border rounded-lg py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>
        
        <div className="max-h-60 overflow-y-auto mb-4">
          {filteredTeamMembers.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No team members found</p>
          ) : (
            <div className="space-y-2">
              {filteredTeamMembers.map(member => (
                <div
                  key={member.id}
                  className={`flex items-center p-3 rounded-lg cursor-pointer ${
                    selectedTeamMember === member.id 
                      ? 'bg-primary/10 border border-primary' 
                      : 'hover:bg-gray-50 border border-transparent'
                  }`}
                  onClick={() => setSelectedTeamMember(member.id)}
                >
                  <div className="flex-shrink-0 h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{member.name}</p>
                    <p className="text-sm text-gray-500">{member.role}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedTeamMember}
          >
            Assign Lead
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignLeadModal;